import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdVisibility, MdClose, MdSearch, MdToggleOn, MdToggleOff } from 'react-icons/md';
import Pagination from '../components/Pagination';
import { formatDate, formatDateTime } from '../utils/format';

export default function Users() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [exportState, setExportState] = useState('Telangana');
  const [exporting, setExporting] = useState(false);

  // Modals
  const [viewModal, setViewModal] = useState(null);
  const [viewStats, setViewStats] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const params = { page, limit: 20 };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const res = await api.get('/admin/users', { params });
      setUsers(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Read URL search/id parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search') || searchParams.get('q') || '';
    if (searchFromUrl) {
      setSearchInput(searchFromUrl);
      setSearchQuery(searchFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const idFromUrl = searchParams.get('id');
    if (idFromUrl && users.length > 0) {
      const match = users.find(u => String(u.id) === String(idFromUrl));
      if (match) {
        openView(match);
      }
    }
  }, [location.search, users]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // View user + stats
  const openView = async (user) => {
    setViewModal(user);
    setViewStats(null);
    try {
      const res = await api.get(`/admin/users/${user.id}/stats`);
      setViewStats(res.data.data);
    } catch {
      setViewStats({ reads: 0, likes: 0, comments: 0, bookmarks: 0 });
    }
  };

  // Edit
  const openEdit = (user) => {
    setEditModal(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      city: user.city || '',
      state: user.state || '',
      region_code: user.region_code || '',
      is_active: user.is_active,
    });
  };

  const saveEdit = async () => {
    if (!editModal) return;
    try {
      await api.put(`/admin/users/${editModal.id}`, editForm);
      toast.success('User updated');
      setEditModal(null);
      fetchUsers(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  // Delete
  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteModal.id}`);
      toast.success('User deleted');
      setDeleteModal(null);
      fetchUsers(pagination.page);
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Toggle active
  const toggleActive = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      fetchUsers(pagination.page);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/users/export-city-users', {
        params: { state: exportState },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${exportState.replace(/\s+/g, '')}_City_User_Count.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported successfully');
    } catch (err) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Users</h2>
        <span className="text-muted">
          {!searchQuery.trim()
            ? `${pagination.total} Total Users`
            : (users.some(u =>
              u.city?.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
              u.region_code?.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
              u.state?.toLowerCase().includes(searchQuery.trim().toLowerCase())
            ) || pagination.total === 0)
              ? `${pagination.total} Users Found`
              : `${pagination.total} total users`
          }
        </span>
      </div>

      <div className="filter-bar">
        <div className="search-input-wrap">
          <MdSearch className="search-icon" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, phone, or email..."
            className="search-input"
          />
        </div>
        <div className="export-section" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
          <label style={{ margin: 0, fontWeight: 500 }}>State:</label>
          <select
            value={exportState}
            onChange={e => setExportState(e.target.value)}
            className="form-control"
            style={{ width: 'auto', padding: '0.4rem 2rem 0.4rem 0.8rem', minWidth: '150px' }}
          >
            <option value="Telangana">Telangana</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
          </select>
          <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting...' : '📥 Export Excel'}
          </button>
        </div>
      </div>

      {loading ? <div className="page-loader">Loading...</div> : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th className="hide-mobile">Email</th>
                  <th className="hide-mobile">City</th>
                  <th className="hide-mobile">Region</th>
                  <th>Status</th>
                  <th className="hide-mobile">Registered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><span className="article-title">{u.name}</span></td>
                    <td>{u.phone}</td>
                    <td className="hide-mobile">{u.email}</td>
                    <td className="hide-mobile">{u.city}</td>
                    <td className="hide-mobile"><span className="badge">{u.region_code}</span></td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-live' : 'badge-expired'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="hide-mobile">
                      <span className="cell-sub">{formatDate(u.created_at)}</span>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="btn btn-sm btn-ghost" onClick={() => openView(u)} title="View"><MdVisibility /></button>
                        <button className="btn btn-sm btn-ghost" onClick={() => openEdit(u)} title="Edit"><MdEdit /></button>
                        <button className="btn btn-sm btn-ghost" onClick={() => toggleActive(u)} title={u.is_active ? 'Deactivate' : 'Activate'}>
                          {u.is_active ? <MdToggleOn style={{ color: '#10B981', fontSize: 20 }} /> : <MdToggleOff style={{ fontSize: 20 }} />}
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(u)} title="Delete"><MdDelete /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan="8" className="empty-row">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mobile-cards">
            {users.map(u => (
              <div key={u.id} className="mobile-card">
                <div className="mobile-card-header">
                  <span className={`badge ${u.is_active ? 'badge-live' : 'badge-expired'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="badge">{u.region_code}</span>
                </div>
                <h4>{u.name}</h4>
                <div className="mobile-card-stats">
                  <span>{u.phone}</span>
                  <span>{u.city}</span>
                </div>
                <div className="action-btns">
                  <button className="btn btn-sm btn-ghost" onClick={() => openView(u)}><MdVisibility /> View</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => openEdit(u)}><MdEdit /> Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(u)}><MdDelete /> Delete</button>
                </div>
              </div>
            ))}
          </div>

          <Pagination pagination={pagination} onPageChange={fetchUsers} />
        </>
      )}

      {/* View User Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button className="modal-close" onClick={() => setViewModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="detail-row"><span className="detail-label">Name</span><span>{viewModal.name}</span></div>
              <div className="detail-row"><span className="detail-label">Phone</span><span>{viewModal.phone}</span></div>
              <div className="detail-row"><span className="detail-label">Email</span><span>{viewModal.email}</span></div>
              <div className="detail-row"><span className="detail-label">City</span><span>{viewModal.city}</span></div>
              <div className="detail-row"><span className="detail-label">State</span><span>{viewModal.state || '-'}</span></div>
              <div className="detail-row"><span className="detail-label">Region Code</span><span className="badge">{viewModal.region_code}</span></div>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <span className={`badge ${viewModal.is_active ? 'badge-live' : 'badge-expired'}`}>
                  {viewModal.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="detail-row"><span className="detail-label">Registered</span><span>{formatDateTime(viewModal.created_at)}</span></div>

              {viewStats && (
                <div className="stats-grid" style={{ marginTop: 16 }}>
                  <div className="stat-card">
                    <div className="stat-info"><h3>{viewStats.reads}</h3><p>Articles Read</p></div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info"><h3>{viewStats.likes}</h3><p>Likes</p></div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info"><h3>{viewStats.comments}</h3><p>Comments</p></div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info"><h3>{viewStats.bookmarks}</h3><p>Bookmarks</p></div>
                  </div>
                </div>
              )}
              {!viewStats && <div className="page-loader">Loading stats...</div>}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="modal-close" onClick={() => setEditModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} maxLength={10} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input value={editForm.state} onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Region Code</label>
                  <input value={editForm.region_code} onChange={e => setEditForm(f => ({ ...f, region_code: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={editForm.is_active ? 'active' : 'inactive'} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value === 'active' }))}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete User</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this user?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>{deleteModal.name} ({deleteModal.phone})</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
